"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useEffect, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSafeI18nDate } from "@/i18n/use-safe-i18n-date";
import {
  ARTIFACT_INVALID_DATE_LABEL,
  formatArtifactUtcCompact,
  formatArtifactUtcFull,
  isArtifactDateValid,
  normalizeArtifactDateLabel,
} from "@/lib/artifacts/format-artifact-date";
import { cn } from "@/lib/utils";

function useArtifactTimestamp(value: string) {
  const { safeI18nDate } = useSafeI18nDate();
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const valid = isArtifactDateValid(value);
  if (!valid) {
    return {
      valid,
      readable: ARTIFACT_INVALID_DATE_LABEL,
      tooltip: ARTIFACT_INVALID_DATE_LABEL,
    };
  }

  const readable = isClient
    ? normalizeArtifactDateLabel(
        safeI18nDate(value, { dateStyle: "medium", timeStyle: "short" }),
        value,
      )
    : formatArtifactUtcCompact(value);
  const tooltip = isClient
    ? normalizeArtifactDateLabel(
        safeI18nDate(value, { dateStyle: "full", timeStyle: "long" }),
        value,
      )
    : formatArtifactUtcFull(value);

  return { valid, readable, tooltip };
}

type ArtifactDateTimeProps = {
  label: string;
  value: string;
  className?: string;
};

/** Readable artifact timestamp with machine-readable datetime and full tooltip. */
export function ArtifactDateTime({
  label,
  value,
  className,
}: ArtifactDateTimeProps) {
  const { valid, readable, tooltip } = useArtifactTimestamp(value);

  const content = (
    <span className={cn("inline-flex items-center gap-1", className)}>
      <span>{label}</span>
      <time
        dateTime={valid ? value : undefined}
        title={valid ? tooltip : undefined}
        className="tabular-nums"
      >
        {readable}
      </time>
    </span>
  );

  if (!valid) return content;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{content}</span>
      </TooltipTrigger>
      <TooltipContent>
        <p>{label}</p>
        <p className="tabular-nums">{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
}

type ArtifactLastUpdatedProps = {
  value: string;
  className?: string;
};

/** List-friendly last-updated label using compact locale formatting. */
export function ArtifactLastUpdated({
  value,
  className,
}: ArtifactLastUpdatedProps) {
  const { i18n } = useLingui();

  return (
    <ArtifactDateTime
      label={i18n._(msg`Last updated`)}
      value={value}
      className={className}
    />
  );
}
